/**
 * @file DownloadDropdown.tsx
 * @module DownloadDropdown
 * @description
 *   A dropdown component for downloading campaign assets individually or as a zip file.
 *   Uses Katal StyledDropdown component for consistent UI and accessibility.
 */

import { useCallback } from 'react';
import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { KatDropdown } from '@amzn/katal-react';
import { DateTime } from 'luxon';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetStatus, PreviewAssets } from 'src/types';
import { DownloadConstants } from 'src/constants';
import { downloadFromUrl, downloadZipFromUrls } from 'src/utils/downloadUtils';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { logger } from 'src/logger';
import { useToast } from 'src/contexts/ToastContext';
import styles from './DownloadDropdown.module.css';

/**
 * Props for the DownloadDropdown component
 * @interface DownloadDropdownProps
 * @property {PreviewAssets} assetUrls - URLs for downloading assets
 */
interface DownloadDropdownProps {
  assetUrls: PreviewAssets;
}

/**
 * DownloadDropdown component for downloading campaign assets
 */
const DownloadDropdown = ({ assetUrls }: DownloadDropdownProps) => {
  const { assetStates } = useAssetContext();
  const { addToast } = useToast();

  const getFileExtension = (type: AssetType) => DownloadConstants.ASSET_TYPE_EXTENSIONS[type];

  const getTimestamp = () => DateTime.now().toFormat('yyyy-MM-dd_HH-mm-ss');

  const downloadAll = useCallback(async (): Promise<void> => {
    try {
      const timestamp = getTimestamp();
      const urlMap = Object.entries(assetStates)
        .filter(
          ([type, state]) =>
            state?.status === AssetStatus.COMPLETED && state?.key && assetUrls[type as AssetType],
        )
        .reduce(
          (acc, [type]) => ({
            ...acc,
            [`${type.toLowerCase()}_${timestamp}.${getFileExtension(type as AssetType)}`]:
              assetUrls[type as AssetType]!,
          }),
          {} as Record<string, string>,
        );

      const zipFilename = `${DownloadConstants.ZIP_FILENAME_PREFIX}_${timestamp}.zip`;
      await downloadZipFromUrls(urlMap, zipFilename);
    } catch (error) {
      logger.error(DownloadConstants.DOWNLOAD_FAILED, error as Record<string, unknown>);
      addToast({
        description: DownloadConstants.DOWNLOAD_FAILED,
        variant: 'danger',
      });
    }
  }, [assetUrls, assetStates]);

  const handleAssetDownload = useCallback(
    async (type: AssetType): Promise<void> => {
      const state = assetStates[type];
      const url = assetUrls[type];

      if (state?.status === AssetStatus.COMPLETED && state?.key && url) {
        try {
          const filename = `${type.toLowerCase()}_${getTimestamp()}.${getFileExtension(type)}`;
          await downloadFromUrl(url, filename);
        } catch (error) {
          logger.error(DownloadConstants.DOWNLOAD_FAILED, error as Record<string, unknown>);
          addToast({
            description: DownloadConstants.DOWNLOAD_FAILED,
            variant: 'danger',
          });
        }
      }
    },
    [assetUrls, assetStates],
  );

  const options = [
    ...[AssetType.BACKGROUND_IMAGE, AssetType.LOGO, AssetType.VIDEO_TRAILER]
      .filter((assetType) => assetUrls[assetType])
      .map((assetType) => {
        const state = assetStates[assetType];
        return {
          name: DownloadConstants.ASSET_TYPE_LABELS[assetType],
          value: assetType,
          disabled: !state || state.status !== AssetStatus.COMPLETED || !state.key,
        };
      }),
    {
      name: DownloadConstants.LABELS.DOWNLOAD_ALL,
      value: DownloadConstants.DOWNLOAD_ALL_VALUE,
      disabled: !Object.values(assetStates).some(
        (state) => state?.status === AssetStatus.COMPLETED && state?.key,
      ),
    },
  ];

  const handleChange = useCallback(
    (event: KatDropdown.ChangeEvent) => {
      const value = event.detail.value;

      const dropdown = event.target as HTMLSelectElement | null;
      if (dropdown) {
        dropdown.value = '';
      }

      if (value === DownloadConstants.DOWNLOAD_ALL_VALUE) {
        downloadAll();
      } else {
        handleAssetDownload(value as AssetType);
      }
    },
    [downloadAll, handleAssetDownload],
  );

  return (
    <StyledDropdown
      placeholder={DownloadConstants.LABELS.DOWNLOAD_ASSETS}
      options={options}
      onChange={handleChange}
      data-testid="download-assets-dropdown"
      className={styles.dropdown}
    />
  );
};

DownloadDropdown.displayName = 'DownloadDropdown';

export { DownloadDropdown };
